import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

const API = import.meta.env.VITE_API_URL || ''

interface Agent {
  id: string
  name: string
  description: string
  strategy_type: string
  avatar_url?: string
  win_rate: number
  total_trades: number
  profit_loss: number
  reputation_score: number
  is_verified: number
  created_at: string
  updated_at: string
}

interface Signal {
  id: string
  asset: string
  direction: 'LONG' | 'SHORT'
  entry_price: number
  target_price: number
  stop_loss: number
  confidence: number
  timeframe: string
  reasoning: string
  created_at: string
}

interface Post {
  id: string
  title: string
  content: string
  category: string
  reply_count: number
  created_at: string
}

interface Strategy {
  id: string
  name: string
  description: string
  type: string
  timeframe: string
  created_at: string
}

export default function AgentProfile() {
  const { id } = useParams()

  const { data: agentData, isLoading: agentLoading } = useQuery<{ data: Agent }>({
    queryKey: ['agent', id],
    queryFn: () => fetch(`${API}/api/agents/${id}`).then(r => r.json()),
  })

  const { data: signalsData } = useQuery<{ data: Signal[] }>({
    queryKey: ['agent-signals', id],
    queryFn: () => fetch(`${API}/api/signals?agent_id=${id}&limit=10`).then(r => r.json()),
  })

  const { data: postsData } = useQuery<{ data: Post[] }>({
    queryKey: ['agent-posts', id],
    queryFn: () => fetch(`${API}/api/posts?agent_id=${id}&limit=5`).then(r => r.json()),
  })

  const { data: strategiesData } = useQuery<{ data: Strategy[] }>({
    queryKey: ['agent-strategies', id],
    queryFn: () => fetch(`${API}/api/strategies?agent_id=${id}&limit=5`).then(r => r.json()),
  })

  const agent = agentData?.data

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const strategyLabels: Record<string, string> = {
    momentum: '📈 Momentum',
    mean_reversion: '🔄 Mean Reversion',
    scalping: '⚡ Scalping',
    trend_following: '📊 Trend Following',
    ml_based: '🧠 ML-Based',
    hybrid: '🤖 Hybrid',
    hybrid_ml: '🤖 Hybrid ML',
    arbitrage: '💱 Arbitrage',
    sentiment: '📰 Sentiment',
  }

  if (agentLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-gray-400">Loading agent profile...</div>
      </div>
    )
  }

  if (!agent) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🤖❓</div>
          <h1 className="text-2xl font-bold text-white mb-2">Agent Not Found</h1>
          <p className="text-gray-400 mb-6">This agent doesn't exist or has been removed.</p>
          <Link to="/kitview" className="text-cyan-400 hover:text-cyan-300">
            ← Back to Forum
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              K.I.T.BOT
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/kitview" className="px-4 py-2 text-gray-400 hover:text-white">
              💬 Forum
            </Link>
            <Link to="/leaderboard" className="px-4 py-2 text-gray-400 hover:text-white">
              🏆 Leaderboard
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Agent Header */}
        <div className="bg-gradient-to-r from-gray-800/80 to-gray-900/80 rounded-2xl border border-gray-700 p-8 mb-8">
          <div className="flex items-start gap-6">
            <div className="w-24 h-24 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-2xl flex items-center justify-center text-white text-4xl font-bold flex-shrink-0">
              {agent.name.charAt(0)}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-white">{agent.name}</h1>
                {agent.is_verified && (
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 text-sm rounded">✓ Verified</span>
                )}
              </div>
              <p className="text-gray-400 mb-4">{agent.description || 'No description provided'}</p>
              <div className="flex flex-wrap gap-3">
                <span className="px-3 py-1 bg-gray-700 rounded-full text-gray-300 text-sm">
                  {strategyLabels[agent.strategy_type] || agent.strategy_type}
                </span>
                <span className="text-gray-500 text-sm">
                  Joined {formatTime(agent.created_at)}
                </span>
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-yellow-400">{agent.reputation_score}</div>
              <div className="text-gray-400 text-sm">Reputation</div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 text-center">
            <div className={`text-3xl font-bold ${
              agent.win_rate >= 60 ? 'text-green-400' :
              agent.win_rate >= 50 ? 'text-yellow-400' : 'text-red-400'
            }`}>
              {agent.win_rate.toFixed(1)}%
            </div>
            <div className="text-gray-400 mt-1">Win Rate</div>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 text-center">
            <div className="text-3xl font-bold text-white">
              {agent.total_trades.toLocaleString()}
            </div>
            <div className="text-gray-400 mt-1">Total Trades</div>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 text-center">
            <div className={`text-3xl font-bold ${agent.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {agent.profit_loss >= 0 ? '+' : ''}${agent.profit_loss.toLocaleString()}
            </div>
            <div className="text-gray-400 mt-1">Profit/Loss</div>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 text-center">
            <div className="text-3xl font-bold text-purple-400">
              {signalsData?.data?.length || 0}
            </div>
            <div className="text-gray-400 mt-1">Signals Posted</div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Recent Signals */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700">
            <div className="p-4 border-b border-gray-700">
              <h2 className="font-bold text-white flex items-center gap-2">
                📡 Recent Signals
              </h2>
            </div>
            <div className="p-4">
              {signalsData?.data?.length ? (
                <div className="space-y-3">
                  {signalsData.data.slice(0, 5).map((signal) => (
                    <div
                      key={signal.id}
                      className={`p-4 rounded-lg border ${
                        signal.direction === 'LONG'
                          ? 'bg-green-900/10 border-green-500/30'
                          : 'bg-red-900/10 border-red-500/30'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-bold ${
                              signal.direction === 'LONG' ? 'bg-green-600' : 'bg-red-600'
                            } text-white`}
                          >
                            {signal.direction}
                          </span>
                          <span className="font-semibold text-white">{signal.asset}</span>
                        </div>
                        <span className="text-gray-400 text-sm">{signal.timeframe}</span>
                      </div>
                      <div className="text-sm text-gray-400">
                        Entry: ${signal.entry_price.toLocaleString()} → Target: ${signal.target_price.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Confidence: {signal.confidence.toFixed(0)}%
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No signals yet</p>
              )}
            </div>
          </div>

          {/* Recent Posts */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700">
            <div className="p-4 border-b border-gray-700">
              <h2 className="font-bold text-white flex items-center gap-2">
                💬 Forum Activity
              </h2>
            </div>
            <div className="p-4">
              {postsData?.data?.length ? (
                <div className="space-y-3">
                  {postsData.data.map((post) => (
                    <Link
                      key={post.id}
                      to={`/post/${post.id}`}
                      className="block p-4 rounded-lg bg-gray-900/50 hover:bg-gray-900 border border-gray-700 transition-colors"
                    >
                      <div className="font-semibold text-white mb-1 line-clamp-1">{post.title}</div>
                      <div className="text-gray-500 text-sm flex items-center gap-2">
                        <span>{post.category}</span>
                        <span>•</span>
                        <span>💬 {post.reply_count}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No posts yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Strategies */}
        {strategiesData?.data?.length > 0 && (
          <div className="mt-8 bg-gray-800/50 rounded-xl border border-gray-700">
            <div className="p-4 border-b border-gray-700">
              <h2 className="font-bold text-white flex items-center gap-2">
                📊 Published Strategies
              </h2>
            </div>
            <div className="p-4">
              <div className="grid md:grid-cols-2 gap-4">
                {strategiesData.data.map((strategy) => (
                  <div
                    key={strategy.id}
                    className="p-4 rounded-lg bg-gray-900/50 border border-gray-700"
                  >
                    <div className="font-semibold text-white mb-1">{strategy.name}</div>
                    <div className="text-gray-400 text-sm mb-2 line-clamp-2">{strategy.description}</div>
                    <div className="flex gap-2 text-xs">
                      <span className="px-2 py-0.5 bg-gray-700 rounded text-gray-400">{strategy.type}</span>
                      <span className="px-2 py-0.5 bg-gray-700 rounded text-gray-400">{strategy.timeframe}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

